/* eslint-disable @atlaskit/editor/no-re-export */
// Mapping file
import type React from 'react';

import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import Loadable from 'react-loadable';

import type { DatasourceAttributeProperties } from '@atlaskit/adf-schema/block-card';
import type { Layout as RichMediaLayout } from '@atlaskit/adf-schema/rich-media-common';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { MediaInlineAttrs } from '@atlaskit/editor-common/media-inline/types';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import UnsupportedBlock from '@atlaskit/editor-common/UnsupportedBlock';
import UnsupportedInline from '@atlaskit/editor-common/UnsupportedInline';
import type { Diff } from '@atlaskit/editor-common/utils/types';
import type { Fragment, Node, Mark } from '@atlaskit/editor-prosemirror/model';
import type { MediaFeatureFlags } from '@atlaskit/media-common/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { MediaSSR } from '../../types/mediaOptions';
import type { SmartLinksOptions } from '../../types/smartLinksOptions';
import type ExpandComponent from '../../ui/Expand';
import type { ExpandProps } from '../../ui/Expand';
import type { NodeComponentsProps, RendererAppearance } from '../../ui/Renderer/types';
import type { WithSmartCardStorageProps } from '../../ui/SmartCardStorage';
import type { NodeProps } from '../types';
import type BlockCardComponent from './blockCard';
import Blockquote from './blockquote';
import BodiedExtension from './bodiedExtension';
import type BodiedSyncBlockComponent from './bodiedSyncBlock';
import BulletList from './bulletList';
import Caption from './caption';
import type CodeBlockComponent from './codeBlock/codeBlock';
import type { Props as CodeBlockNodeProps } from './codeBlock/codeBlock';
import type WindowedCodeBlockComponent from './codeBlock/windowedCodeBlock';
// Ignored via go/ees005
// eslint-disable-next-line import/no-named-as-default
import type DateComponent from './date';
import type { Props as DateNodeProps } from './date';
import type DecisionItemComponent from './decisionItem';
import type DecisionListComponent from './decisionList';
import type { Props as DecisionListNodeProps } from './decisionList';
import Doc, { DocWithSelectAllTrap } from './doc';
import type EmbedCardComponent from './embedCard';
import type EmojiComponent from './emoji';
import type { EmojiProps } from './emoji';
import Extension from './extension';
import ExtensionFrame from './extensionFrame';
import HardBreak from './hardBreak';
import Heading from './heading';
import type InlineCardComponent from './inlineCard';
import type { InlineCardProps } from './inlineCard';
import InlineExtension from './inlineExtension';
import LayoutColumn from './layoutColumn';
import LayoutSection from './layoutSection';
import ListItem from './listItem';
import type MediaComponent from './media';
import type { MediaProps } from './media';
import type MediaGroupComponent from './mediaGroup';
import type { MediaGroupProps } from './mediaGroup';
import type MediaInlineComponent from './mediaInline';
import type MediaSingleComponent from './mediaSingle';
import type { Props as MediaSingleNodeProps } from './mediaSingle';
import type MentionComponent from './mention';
import type { Props as MentionNodeProps } from './mention';
import MultiBodiedExtension from './multiBodiedExtension';
import OrderedList from './orderedList';
import type PanelComponent from './panel';
import type { Props as PanelNodeProps } from './panel';
import Paragraph from './paragraph';
import Placeholder from './placeholder';
import Rule from './rule';
import type StatusComponent from './status';
import type { Props as StatusNodeProps } from './status';
import type SyncBlock from './syncBlock';
import Table from './table';
import { TableCell, TableHeader } from './tableCell';
import TableRow from './tableRow';
import type TaskItemComponent from './taskItem';
import type { Props as TaskItemNodeProps } from './taskItem';
import type TaskListComponent from './taskList';
import type { Props as TaskListNodeProps } from './taskList';
import UnknownBlock from './unknownBlock';

const WindowedCodeBlock: React.ComponentType<CodeBlockNodeProps> & Loadable.LoadableComponent =
	Loadable({
		loader: () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_renderer-node_WindowedCodeBlock" */
				'./codeBlock/windowedCodeBlock'
			).then((mod) => mod.default) as Promise<typeof WindowedCodeBlockComponent>,
		loading: () => null,
	});

const CodeBlock: React.ComponentType<WithIntlProps<CodeBlockNodeProps & WrappedComponentProps>> &
	Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_CodeBlock" */
			'./codeBlock/codeBlock'
		).then((mod) => mod.default) as Promise<typeof CodeBlockComponent>,
	loading: () => null,
});

const TaskList: React.ComponentType<TaskListNodeProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_TaskList" */
			'./taskList'
		).then((mod) => mod.default) as Promise<typeof TaskListComponent>,
	loading: () => null,
});

const TaskItem: React.ComponentType<NodeProps<TaskItemNodeProps>> & Loadable.LoadableComponent =
	Loadable({
		loader: () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_renderer-node_TaskItem" */
				'./taskItem'
			).then((mod) => mod.default) as Promise<typeof TaskItemComponent>,
		loading: () => null,
	});

const DecisionList: React.ComponentType<DecisionListNodeProps> & Loadable.LoadableComponent =
	Loadable({
		loader: () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_renderer-node_DecisionList" */
				'./decisionList'
			).then((mod) => mod.default) as Promise<typeof DecisionListComponent>,
		loading: () => null,
	});

const DecisionItem: React.ComponentType<NodeProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_DecisionItem" */
			'./decisionItem'
		).then((mod) => mod.default) as Promise<typeof DecisionItemComponent>,
	loading: () => null,
});

const Date: React.ComponentType<DateNodeProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_Date" */
			'./date'
		).then((mod) => mod.default) as Promise<typeof DateComponent>,
	loading: () => null,
});

const Status: React.ComponentType<StatusNodeProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_Status" */
			'./status'
		).then((mod) => mod.default) as Promise<typeof StatusComponent>,
	loading: () => null,
});

const Emoji: React.ComponentType<EmojiProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_Emoji" */
			'./emoji'
		).then((mod) => mod.default) as Promise<typeof EmojiComponent>,
	loading: () => null,
});

const Panel: React.ComponentType<PanelNodeProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_Panel" */
			'./panel'
		).then((mod) => mod.default) as Promise<typeof PanelComponent>,
	loading: () => null,
});

const EmbedCard: React.ComponentType<{
	data?: object;
	eventHandlers?: EventHandlers;
	isInsideOfBlockNode?: boolean;
	isInsideOfInlineExtension?: boolean;
	layout: RichMediaLayout;
	onSetLinkTarget?: (url: string) => '_blank' | undefined;
	originalHeight?: number;
	originalWidth?: number;
	portal?: HTMLElement;
	rendererAppearance?: RendererAppearance;
	smartLinks?: SmartLinksOptions;
	url?: string;
	width?: number;
}> &
	Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_EmbedCard" */
			'./embedCard'
		).then((mod) => mod.default) as Promise<typeof EmbedCardComponent>,
	loading: () => null,
});

const InlineCard: React.ComponentType<
	Diff<InlineCardProps & WithSmartCardStorageProps, WithSmartCardStorageProps>
> &
	Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_InlineCard" */
			'./inlineCard'
		).then((mod) => mod.default) as Promise<typeof InlineCardComponent>,
	loading: () => null,
});

const BlockCard: React.ComponentType<{
	data?: object;
	datasource?: DatasourceAttributeProperties;
	eventHandlers?: EventHandlers;
	isNodeNested?: boolean;
	layout?: string;
	localId?: string;
	onSetLinkTarget?: (url: string) => '_blank' | undefined;
	portal?: HTMLElement;
	rendererAppearance?: RendererAppearance;
	smartLinks?: SmartLinksOptions;
	url?: string;
}> &
	Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_BlockCard" */
			'./blockCard'
		).then((mod) => mod.default) as Promise<typeof BlockCardComponent>,
	loading: () => null,
});

const Media: React.ComponentType<React.PropsWithChildren<MediaProps>> & Loadable.LoadableComponent =
	Loadable({
		loader: () =>
			import(
				/* webpackChunkName: "@atlaskit-internal_renderer-node_Media" */
				'./media'
			).then((mod) => mod.default) as Promise<typeof MediaComponent>,
		loading: () => null,
	});

const MediaGroup: React.ComponentType<MediaGroupProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_MediaGroup" */
			'./mediaGroup'
		).then((mod) => mod.default) as Promise<typeof MediaGroupComponent>,
	loading: () => null,
});

const MediaInline: React.ComponentType<
	WithIntlProps<
		{
			collection?: string;
			eventHandlers?: EventHandlers;
			fallbackMediaNameFetcher?: (id: string) => Promise<string>;
			featureFlags?: MediaFeatureFlags;
			id: string;
			marks?: Array<Mark>;
			providers: ProviderFactory;
			rendererAppearance?: RendererAppearance;
			ssr?: MediaSSR;
		} & WrappedComponentProps &
			MediaInlineAttrs
	>
> &
	Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_MediaInline" */
			'./mediaInline'
		).then((mod) => mod.default) as Promise<typeof MediaInlineComponent>,
	loading: () => null,
});

const MediaSingle: React.ComponentType<
	WithIntlProps<MediaSingleNodeProps & WrappedComponentProps>
> &
	Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_MediaSingle" */
			'./mediaSingle'
		).then((mod) => mod.default) as Promise<typeof MediaSingleComponent>,
	loading: () => null,
});

const Mention: React.ComponentType<MentionNodeProps> & Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_Mention" */
			'./mention'
		).then((mod) => mod.default) as Promise<typeof MentionComponent>,
	loading: () => null,
});

const Expand: React.ComponentType<WithIntlProps<ExpandProps & WrappedComponentProps>> &
	Loadable.LoadableComponent = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_Expand" */
			'../../ui/Expand'
		).then((mod) => mod.default) as Promise<typeof ExpandComponent>,
	loading: () => null,
});

const SyncBlockLoader = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_SyncBlock" */
			'./syncBlock'
		).then((mod) => mod.default) as Promise<typeof SyncBlock>,
	loading: () => null,
});

const BodiedSyncBlock = Loadable({
	loader: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_renderer-node_BodiedSyncBlock" */ './bodiedSyncBlock'
		).then((mod) => mod.default) as Promise<typeof BodiedSyncBlockComponent>,
	loading: () => null,
});

export const nodeToReact: {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: React.ComponentType<React.PropsWithChildren<any>>;
} = {
	blockquote: Blockquote,
	bulletList: BulletList,
	blockCard: BlockCard,
	caption: Caption,
	date: Date,
	decisionItem: DecisionItem,
	decisionList: DecisionList,
	doc: Doc,
	emoji: Emoji,
	extension: Extension,
	bodiedExtension: BodiedExtension,
	multiBodiedExtension: MultiBodiedExtension,
	extensionFrame: ExtensionFrame,
	hardBreak: HardBreak,
	heading: Heading,
	inlineCard: InlineCard,
	inlineExtension: InlineExtension,
	layoutSection: LayoutSection,
	layoutColumn: LayoutColumn,
	listItem: ListItem,
	media: Media,
	mediaGroup: MediaGroup,
	mediaInline: MediaInline,
	mediaSingle: MediaSingle,
	mention: Mention,
	orderedList: OrderedList,
	panel: Panel,
	panel_c1: Panel,
	paragraph: Paragraph,
	placeholder: Placeholder,
	rule: Rule,
	status: Status,
	taskItem: TaskItem,
	taskList: TaskList,
	table: Table,
	tableCell: TableCell,
	tableHeader: TableHeader,
	tableRow: TableRow,
	unknownBlock: UnknownBlock,
	unsupportedBlock: UnsupportedBlock,
	unsupportedInline: UnsupportedInline,
	expand: Expand,
	syncBlock: SyncBlockLoader,
	bodiedSyncBlock: BodiedSyncBlock,
	nestedExpand: Expand,
	embedCard: EmbedCard,
	blockTaskItem: TaskItem,
};

interface ToReactFlags {
	allowSelectAllTrap?: boolean;
	allowWindowedCodeBlock?: boolean;
}

export const toReact = (
	node: Node,
	flags?: ToReactFlags,
	nodeComponents?: NodeComponentsProps,
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): React.ComponentType<React.PropsWithChildren<any>> => {
	if (node.type.name === 'doc' && flags?.allowSelectAllTrap === true) {
		return DocWithSelectAllTrap;
	}

	// Custom components provide the node implementations used by the renderer.
	const nodes = {
		...(isExperimentEnabled('platform_editor_remove_default_renderer_nodes') ? {} : nodeToReact),
		...nodeComponents,
	};

	if (node.type.name === 'codeBlock') {
		if (flags?.allowWindowedCodeBlock === true) {
			return nodes.windowedCodeBlock ?? WindowedCodeBlock;
		}
		return nodes.codeBlock ?? CodeBlock;
	}

	nodes['multiBodiedExtension'] = MultiBodiedExtension;
	return nodes[node.type.name];
};

export interface TextWrapper {
	content: Node[];
	nodeSize: number;
	type: {
		name: 'textWrapper';
	};
}

interface NodeSimple {
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	attrs?: any;
	nodeSize: number;
	text?: string;
	type: {
		name: string;
	};
}

/*
 *  Wraps adjacent textnodes in a textWrapper
 *
 *  Input:
 *  [
 *    {
 *      type: 'text',
 *      text: 'Hello'
 *    },
 *    {
 *      type: 'text',
 *      text: 'World!',
 *      marks: [
 *        {
 *          type: 'strong'
 *        }
 *      ]
 *    }
 *  ]
 *
 *  Output:
 *  [
 *    {
 *      type: 'textWrapper',
 *      content: [
 *        {
 *          type: 'text',
 *          text: 'Hello'
 *        },
 *        {
 *          type: 'text',
 *          text: 'World!',
 *          marks: [
 *            {
 *              type: 'strong'
 *            }
 *          ]
 *        }
 *      ]
 *    }
 *  ]
 */

export const mergeTextNodes = (
	nodes: (Node | NodeSimple)[],
): (Node | TextWrapper | NodeSimple)[] => {
	return nodes.reduce<(TextWrapper | Node | NodeSimple)[]>((acc, current) => {
		if (!isText(current.type.name)) {
			acc.push(current);
			return acc;
		}

		// Append node to previous node, if it was a text wrapper
		if (acc.length > 0 && isTextWrapper(acc[acc.length - 1])) {
			const textWrapper = acc[acc.length - 1] as TextWrapper;
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			textWrapper.content!.push(current as Node);
			textWrapper.nodeSize += current.nodeSize;
		} else {
			acc.push({
				type: {
					name: 'textWrapper',
				},
				content: [current],
				nodeSize: current.nodeSize,
			} as TextWrapper);
		}

		return acc;
	}, []);
};

export const isText = (type: string): type is 'text' => {
	return type === 'text';
};

export const isTextWrapper = (node: Node | TextWrapper | NodeSimple): node is TextWrapper => {
	return node.type.name === 'textWrapper';
};

export function isTextNode(node: Node | Mark): node is Node {
	return node.type.name === 'text';
}

// Ignored via go/ees005
// eslint-disable-next-line require-unicode-regexp
const whitespaceRegex = /^\s*$/;

/**
 * Detects whether a fragment contains a single paragraph node
 * whose content satisfies the condition for an emoji block
 */
export const isEmojiDoc = (doc: Fragment): boolean => {
	if (doc.childCount !== 1) {
		return false;
	}
	const parentNodes: Node[] = [];
	doc.forEach((child) => parentNodes.push(child));
	const node = parentNodes[0];
	return node.type.name === 'paragraph' && isEmojiBlock(node.content);
};

const isEmojiBlock = (pnode: Fragment): boolean => {
	const content: Node[] = [];
	// Optimisation for long documents - worst case block will be space-emoji-space
	if (pnode.childCount > 7) {
		return false;
	}
	pnode.forEach((child) => content.push(child));
	let emojiCount = 0;
	for (let i = 0; i < content.length; ++i) {
		const node = content[i];
		switch (node.type.name) {
			case 'text':
				if (node.text && !node.text.match(whitespaceRegex)) {
					return false;
				}
				continue;
			case 'emoji':
				if (++emojiCount > 3) {
					return false;
				}
				continue;
			default:
				// Only text and emoji nodes are allowed
				return false;
		}
	}
	return emojiCount > 0;
};

export {
	Blockquote,
	BodiedExtension,
	BulletList,
	BlockCard,
	Caption,
	CodeBlock,
	WindowedCodeBlock,
	Date,
	DecisionItem,
	DecisionList,
	Doc,
	DocWithSelectAllTrap,
	Emoji,
	Extension,
	ExtensionFrame,
	Expand,
	HardBreak,
	Heading,
	ListItem,
	InlineCard,
	InlineExtension,
	LayoutSection,
	LayoutColumn,
	Media,
	MediaGroup,
	MediaInline,
	MediaSingle,
	Mention,
	MultiBodiedExtension,
	OrderedList,
	Panel,
	Paragraph,
	Placeholder,
	Rule,
	Status,
	TaskItem,
	TaskList,
	Table,
	TableCell,
	TableRow,
	UnknownBlock,
	EmbedCard,
};
